import { useMemo } from 'react';
import {
  Avatar,
  Chip,
  DetailsPanel,
  RowCardWithAction,
  Typography,
} from '@weareopenr/component-library';
import { IUser } from 'src/types';
import styles from './RecentUsers.module.scss';

export interface RecentUsersProps {
  users: IUser[];
}

const RECENT_USERS_LIMIT = 5;

const formatDate = (value: string): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-GB');
};

// Users created through the WorkOS API (SSO, scripts) can arrive with no
// first or last name, so every name-derived value falls back to the email.
const nameParts = (user: IUser): string[] =>
  [user.first_name, user.last_name].filter((part): part is string => Boolean(part));

const fullName = (user: IUser): string => nameParts(user).join(' ') || user.email;

const initials = (user: IUser): string => {
  const letters = nameParts(user)
    .map((part) => part.charAt(0))
    .join('');
  return (letters || user.email.charAt(0)).toUpperCase();
};

const capitalise = (value: string | null | undefined): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

export const RecentUsers = ({ users }: RecentUsersProps) => {
  const recentUsers = useMemo(
    () =>
      [...users]
        .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
        .slice(0, RECENT_USERS_LIMIT),
    [users],
  );

  return (
    <DetailsPanel title="Recently added users">
      {recentUsers.length === 0 ? (
        <Typography colour="secondary">No users added yet.</Typography>
      ) : (
        <ul className={styles.list}>
          {recentUsers.map((user) => (
            <li key={user.id}>
              <RowCardWithAction
                id={user.id}
                leading={<Avatar variant="custom" initials={initials(user)} />}
                title={fullName(user)}
                chip={
                  <Chip
                    label={capitalise(user.state)}
                    backgroundColour={user.state === 'active' ? 'kale' : 'liquorice'}
                  />
                }
                meta={[
                  { key: 'email', label: user.email, colour: 'liquorice' },
                  {
                    key: 'joined',
                    label: `Joined ${formatDate(user.created_at)}`,
                    colour: 'liquorice',
                  },
                ]}
                action={{ to: `/users/${user.id}`, label: 'View user' }}
              />
            </li>
          ))}
        </ul>
      )}
    </DetailsPanel>
  );
};
